import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useLanguage } from "@/hooks/useLanguage";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import * as LucideIcons from "lucide-react";
import { 
  User, 
  Award, 
  Coins,
  CalendarDays,
  Copy,
  MessageCircle,
  Share,
  MoreHorizontal
} from "lucide-react";
import { SiFacebook, SiX, SiLinkedin } from "react-icons/si";
import { logger } from "@/lib/logger";

const AuthorSection = () => {
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const [registrationDate, setRegistrationDate] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [displayName, setDisplayName] = useState<string>("");
  const [originalDisplayName, setOriginalDisplayName] = useState<string>("");
  const [referralCode, setReferralCode] = useState<string>("");
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();

  // Fetch user profile information
  const { data: profile } = useQuery({
    queryKey: ['userProfile'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      
      // Set display name from profile
      if (data) {
        setDisplayName(data.display_name || '');
        setOriginalDisplayName(data.display_name || '');
      }
      
      return data;
    }
  });

  // Fetch completed badges
  const { data: badges } = useQuery({
    queryKey: ['userBadges'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('user_badges_progress')
        .select(`
          *,
          badge:badge_id(
            id,
            name,
            description,
            icon_name
          )
        `)
        .eq('user_id', user.id)
        .eq('unlocked', true)
        .order('earned_at', { ascending: false });

      if (error) throw error;
      return data || [];
    }
  });

  // Fetch referral code and user info
  useEffect(() => {
    const fetchUserData = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data, error } = await supabase
          .from('profiles')
          .select('created_at')
          .eq('id', user.id)
          .single();

        if (!error && data?.created_at) {
          setRegistrationDate(data.created_at);
        }
        
        // Fetch or create referral code
        const { data: referralData, error: referralError } = await supabase
          .from('user_referrals')
          .select('referral_code')
          .eq('referrer_id', user.id)
          .limit(1)
          .single();

        if (referralError && referralError.code === 'PGRST116') {
          // No referral code exists, create one
          const { data: newReferral, error: createError } = await supabase
            .from('user_referrals')
            .insert([{ 
              referrer_id: user.id,
              referral_code: null // This will trigger our database function to generate a code
            }])
            .select('referral_code')
            .single();

          if (!createError && newReferral) {
            setReferralCode(newReferral.referral_code);
          }
        } else if (!referralError && referralData) {
          setReferralCode(referralData.referral_code);
        }
      }
    };

    fetchUserData();
  }, []);

  const updateDisplayName = async () => {
    if (!displayName.trim()) {
      toast({
        title: "Invalid Display Name",
        description: "Display name cannot be empty",
        variant: "destructive",
      });
      return;
    }

    if (displayName === originalDisplayName) {
      return;
    }

    setIsUpdating(true);
    try {
      const { data: existingProfiles, error: checkError } = await supabase
        .from('profiles')
        .select('id')
        .ilike('display_name', displayName)
        .neq('id', (await supabase.auth.getUser()).data.user?.id)
        .limit(1);

      if (checkError) throw checkError;

      if (existingProfiles && existingProfiles.length > 0) {
        toast({
          title: "Display Name Taken",
          description: "This display name is already in use. Please choose another one.",
          variant: "destructive",
        });
        return;
      }

      const { error } = await supabase
        .from('profiles')
        .update({ display_name: displayName })
        .eq('id', (await supabase.auth.getUser()).data.user?.id);

      if (error) throw error;

      setOriginalDisplayName(displayName);
      toast({
        title: "Success",
        description: "Display name updated successfully",
      });
    } catch (error: any) {
      logger.error('Error updating display name:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update display name",
      });
      setDisplayName(originalDisplayName);
    } finally {
      setIsUpdating(false);
    }
  };
  
  const copyReferralLink = async () => {
    const referralLink = `${window.location.origin}/invite?ref=${referralCode}`;
    try {
      await navigator.clipboard.writeText(referralLink);
      toast({
        title: t('settings.profile.inviteFriends.copySuccess.title'),
        description: t('settings.profile.inviteFriends.copySuccess.description')
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: t('settings.profile.inviteFriends.copyFailure.title'),
        description: t('settings.profile.inviteFriends.copyFailure.description')
      });
    }
  };

  const shareToSocial = (platform: 'facebook' | 'twitter' | 'linkedin') => {
    const referralLink = `${window.location.origin}/invite?ref=${referralCode}`;
    const message = encodeURIComponent(t('settings.profile.inviteFriends.shareMessage'));
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(referralLink)}&text=${message}`, // The URL remains the same
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralLink)}`,
    };

    window.open(urls[platform], '_blank');
  };

  // Format badge rarity to CSS class
  const getBadgeRarityClass = (rarity: string) => {
    switch (rarity) {
      case 'common': return 'bg-gray-500';
      case 'uncommon': return 'bg-green-500';
      case 'rare': return 'bg-blue-500';
      case 'epic': return 'bg-purple-500';
      case 'legendary': return 'bg-amber-500';
      default: return 'bg-gray-500';
    }
  };

  // Helper function to get icons by name
  const getIconByName = (iconName: string) => {
    if (!iconName) return <Award className="h-4 w-4 mr-2" />;
    
    // Check if icon exists in Lucide
    const IconComponent = (LucideIcons as any)[iconName] || LucideIcons.Award;
    return <IconComponent className="h-4 w-4 mr-2" />;
  };

  const handleDeleteAccount = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    
    setIsDeleting(true);
    try {
      // First check if user has an active subscription
      const { data: subscription, error: subscriptionError } = await supabase
        .from('subscriptions')
        .select('stripe_subscription_id')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .single();
      
      if (subscriptionError && subscriptionError.code !== 'PGRST116') {
        // Only throw if it's not a "no rows returned" error
        throw subscriptionError;
      }
      
      // If user has an active subscription, cancel it first
      if (subscription?.stripe_subscription_id) {
        const { error: cancelError } = await supabase.functions.invoke('cancel-subscription', {
          body: { subscriptionId: subscription.stripe_subscription_id }
        });
        
        if (cancelError) throw cancelError;
      }

      // Then delete the user's data (this will cascade to related tables due to FK constraints)
      const { error: deleteError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', user.id);
        
      if (deleteError) throw deleteError;

      // Delete the user's authentication record using a Supabase Edge Function
      const { error: deleteAuthError } = await supabase.functions.invoke('delete-user-auth', {
        body: { userId: user.id }
      });

      if (deleteAuthError) throw deleteAuthError;

      // Sign out the user
      await supabase.auth.signOut();
      navigate("/");
      
      toast({
        title: t('profile.accountDeleted'),
        description: t('profile.accountDeletedMessage'),
      });
    } catch (error) {
      logger.error('Error deleting account:', error);
      toast({
        title: t('errors.error'),
        description: t('errors.deleteAccountError'),
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Card className="p-2 sm:p-6 py-4 bg-white/5 backdrop-blur-sm border border-white/10">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <User className="h-6 w-6 text-white" />
            <h2 className="text-2xl font-bold text-white">
              {t('dashboard.author.title')}
            </h2>
          </div>
        </div>

        {/* User Info Grid - Responsive layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Profile column - Display name & Invite Friends */}
          <div className="space-y-6">
            {/* Display Name */}
            <div className="space-y-2">
              <Label htmlFor="display-name" className="text-white">
                {t('settings.profile.displayName.label')}
              </Label>
              <div className="flex gap-2">
                <Input
                  id="display-name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="w-full"
                  placeholder={t('settings.profile.displayName.placeholder')}
                  maxLength={500}
                />
                <Button
                  onClick={updateDisplayName}
                  disabled={isUpdating || displayName === originalDisplayName}
                  className="whitespace-nowrap"
                >
                  {t('settings.profile.displayName.updateButton')}
                </Button>
              </div>
              <p className="text-sm text-gray-400">
                {t('settings.profile.displayName.helpText')}
              </p>
            </div>
            
            {/* Invite Friends */}
            <div className="space-y-2">
              <Label className="text-white block mb-2">{t('settings.profile.inviteFriends.label')}</Label>
              <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="secondary" className="w-full sm:w-auto">
                    <Share className="mr-2 h-4 w-4" />
                    {t('settings.profile.inviteFriends.button')}
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>{t('settings.profile.inviteFriends.dialogTitle')}</DialogTitle>
                    <DialogDescription>
                      {t('settings.profile.inviteFriends.dialogDescription')}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="flex items-center space-x-2 mt-4">
                    <Input
                      className="flex-1"
                      value={`${window.location.origin}/invite?ref=${referralCode}`}
                      readOnly
                    />
                    <Button variant="secondary" size="icon" onClick={copyReferralLink}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <DialogFooter className="flex sm:justify-start gap-2 mt-4">
                    <Button
                      variant="outline"
                      onClick={() => shareToSocial('facebook')}
                      className="flex-1"
                    >
                      <SiFacebook className="mr-2 h-4 w-4" />
                      {t('settings.profile.inviteFriends.socialPlatforms.facebook')}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => shareToSocial('twitter')}
                      className="flex-1"
                    >
                      <SiX className="mr-2 h-4 w-4" />
                      {t('settings.profile.inviteFriends.socialPlatforms.X')}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => shareToSocial('linkedin')}
                      className="flex-1"
                    >
                      <SiLinkedin className="mr-2 h-4 w-4" />
                      {t('settings.profile.inviteFriends.socialPlatforms.linkedin')}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              <p className="text-sm text-gray-400">
                {t('settings.profile.inviteFriends.dialogDescription')}
              </p>
            </div>
          </div>
          
          {/* Stats column - Credits & Registration date */}
          <div className="space-y-6">
            {/* Stats cards in a grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Credits card */}
              <div className="flex items-center p-4 bg-white/5 rounded-lg border border-white/10">
                <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 mr-4">
                  <Coins className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <h3 className="font-medium text-white">
                    {t('dashboard.author.credits')}
                  </h3>
                  <p className="text-sm text-gray-400">
                    {t('dashboard.author.creditsAvailable').replace('{{count}}', (profile?.credits || 0).toString())}
                  </p>
                </div>
              </div>

              {/* Registration date card */}
              <div className="flex items-center p-4 bg-white/5 rounded-lg border border-white/10">
                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 mr-4">
                  <CalendarDays className="h-5 w-5 text-amber-500" />
                </div>
                <div>
                  <h3 className="font-medium text-white">
                    {t('dashboard.author.memberSince')}
                  </h3>
                  <p className="text-sm text-gray-400">
                    {registrationDate ? format(new Date(registrationDate), 'PPP') : t('dashboard.author.loading')}
                  </p>
                </div>
              </div>
            </div>

            {/* Badges section */}
            <div className="p-4 bg-white/5 rounded-lg border border-white/10">
              <div className="flex items-center gap-2 mb-3">
                <Award className="h-5 w-5 text-purple-500" />
                <h3 className="font-medium text-white">
                  {t('dashboard.author.recentBadges')}
                </h3>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {badges && badges.length > 0 ? (
                  badges.slice(0, 6).map((badgeProgress) => (
                    <TooltipProvider key={badgeProgress.badge_id}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span>
                            <Badge 
                              variant="outline" 
                              className="cursor-pointer bg-opacity-20 hover:bg-opacity-30 flex items-center"
                            >
                              {getIconByName(badgeProgress.badge.icon_name)}
                              {badgeProgress.badge.name}
                            </Badge>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{badgeProgress.badge.description}</p>
                          <p className="text-xs mt-1">
                            {t('dashboard.author.earnedOn').replace('{{date}}', format(new Date(badgeProgress.earned_at), 'PPP'))}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ))
                ) : (
                  <p className="text-sm text-gray-400">
                    {t('dashboard.author.noBadgesYet')}
                  </p>
                )}
                {badges && badges.length > 6 && (
                  <Badge variant="outline" className="bg-gray-500 bg-opacity-20 flex items-center">
                    <MoreHorizontal className="h-4 w-4 mr-2" />
                    +{badges.length - 6} {t('dashboard.author.more')}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Delete Account Section */}
        <div className="space-y-2">    
          <div className="border-t border-white/10 pt-6">      
            <h2 className="text-xl font-semibold text-white mb-4">
                  {t('profile.dangerZone')}
            </h2>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" className="w-full sm:w-auto">
                  {t('profile.deleteAccount')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('profile.confirmDelete')}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('profile.deleteWarning')}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDeleteAccount} disabled={isDeleting}>
                    {t('profile.confirmDeleteButton')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default AuthorSection;
